import axios from './httpClient';
import { parseAppPath } from './routes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SELECTED_GUILD_KEY = 'tns:selected_guild_id';

const getHeaders = () => ({});

export const progressionRequest = async (path, method = 'get', data, guildId) => {
  const gid = guildId || await _requireGuildId();
  const response = await axios[method](_withGuildId(`${API}${path}`, gid),
    ...(method === 'get' ? [{ headers: getHeaders() }] : [data, { headers: getHeaders() }]));
  return response.data;
};
export const getProgression = (guildId) => progressionRequest('/progression', 'get', undefined, guildId);
export const getRoleAssignmentStatus = (guildId, operationId) => progressionRequest(`/progression/health${operationId ? `?operation_id=${encodeURIComponent(operationId)}` : ''}`, 'get', undefined, guildId);
export const getProgressionOperation = (operationId, guildId) => progressionRequest(`/progression/operations/${encodeURIComponent(operationId)}`, 'get', undefined, guildId);
export const previewProgression = (action, payload, guildId) => progressionRequest('/progression/preview', 'post', { action, payload }, guildId);
export const executeProgression = (token, guildId) => progressionRequest('/progression/execute', 'post', { token }, guildId);
export const previewCurve = (curve, activeHours = 2) => progressionRequest('/progression/curve-preview', 'post', { curve, active_hours: activeHours });
export const getSeasons = (guildId) => progressionRequest('/seasons', 'get', undefined, guildId);
export const createSeason = (data) => progressionRequest('/seasons', 'post', data);
export const updateSeason = (id, data) => progressionRequest(`/seasons/${encodeURIComponent(id)}`, 'patch', data);
export const getSeasonStats = (id) => progressionRequest(`/seasons/${encodeURIComponent(id)}/stats`);

const _getSelectedGuildId = () => {
  if (typeof window === 'undefined') return '';
  const fromPath = parseAppPath(window.location.pathname || '').guildId || '';
  if (fromPath) return String(fromPath);
  return (localStorage.getItem(SELECTED_GUILD_KEY) || '').toString();
};

const _setSelectedGuildId = (guildId) => {
  if (typeof window === 'undefined') return;
  const gid = (guildId || '').toString();
  if (!gid) return;
  localStorage.setItem(SELECTED_GUILD_KEY, gid);
};

export const ensureSelectedGuildId = async () => {
  const existing = _getSelectedGuildId();
  if (existing) {
    _setSelectedGuildId(existing);
    return existing;
  }
  return '';
};

const _requireGuildId = async () => {
  const gid = (await ensureSelectedGuildId()) || '';
  if (!gid) {
    const err = new Error('Missing selected guild');
    err.code = 'NO_GUILD_SELECTED';
    throw err;
  }
  return gid;
};

const _withGuildId = (url, guildId) => {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}guild_id=${encodeURIComponent(guildId)}`;
};

// Settings API
export const getSettings = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/settings`, guildId), { headers: getHeaders() });
  return { ...(response.data || {}), guild_id: guildId };
};

export const getSystemStatus = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/settings/system-status`, guildId), {
    headers: getHeaders(),
  });
  return response.data || {};
};

// Commands System API
export const getCommandsSettings = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/commands/settings`, guildId), {
    headers: getHeaders(),
  });
  return response.data || {};
};

export const updateCommandsSettings = async (updates) => {
  const guildId = await _requireGuildId();
  const response = await axios.patch(
    _withGuildId(`${API}/commands/settings`, guildId),
    updates || {},
    { headers: getHeaders() },
  );
  if (typeof window !== 'undefined' && window?.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('tns:commands-settings-updated', { detail: response.data || {} }));
  }
  return response.data || {};
};

export const updateSettings = async (updates) => {
  const guildId = await _requireGuildId();
  const payload = { ...(updates || {}) };
  delete payload.guild_id;

  const response = await axios.patch(_withGuildId(`${API}/settings`, guildId), payload, { headers: getHeaders() });
  const data = { ...(response.data || {}), guild_id: guildId };

  if (typeof window !== 'undefined' && window?.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('tns:settings-updated', { detail: data }));
  }
  return data;
};

// Level Settings API
export const getLevelSettings = async (capturedGuildId) => {
  const guildId = capturedGuildId || await _requireGuildId();
  const response = await axios.get(`${API}/guilds/${encodeURIComponent(guildId)}/level/settings`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const updateLevelSettings = async (updates, capturedGuildId) => {
  const guildId = capturedGuildId || await _requireGuildId();
  const response = await axios.patch(
    `${API}/guilds/${encodeURIComponent(guildId)}/level/settings`,
    { ...(updates || {}) },
    { headers: getHeaders() }
  );
  if (typeof window !== 'undefined' && window?.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('tns:level-settings-updated', { detail: response.data || {} }));
  }
  return response.data;
};

export const getLevelRankRoles = async (capturedGuildId) => {
  const guildId = capturedGuildId || await _requireGuildId();
  const response = await axios.get(`${API}/guilds/${encodeURIComponent(guildId)}/level/rank-roles`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const previewLeaderboardTheme = async (payload, signal) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(
    `${API}/guilds/${encodeURIComponent(guildId)}/level/preview/leaderboard`,
    { ...(payload || {}) },
    { headers: getHeaders(), signal },
  );
  return response.data || {};
};

export const previewRankCardTheme = async (payload, signal) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(
    `${API}/guilds/${encodeURIComponent(guildId)}/level/preview/rank-card`,
    { ...(payload || {}) },
    { headers: getHeaders(), signal },
  );
  return response.data || {};
};

// Auto Moderation API
export const getAutoModSettings = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(`${API}/guilds/${encodeURIComponent(guildId)}/automod/settings`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const updateAutoModSettings = async (updates) => {
  const guildId = await _requireGuildId();
  const response = await axios.patch(
    `${API}/guilds/${encodeURIComponent(guildId)}/automod/settings`,
    { ...(updates || {}) },
    { headers: getHeaders() },
  );
  if (typeof window !== 'undefined' && window?.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('tns:automod-settings-updated', { detail: response.data || {} }));
  }
  return response.data;
};

export const getAutoModBadWords = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(`${API}/guilds/${encodeURIComponent(guildId)}/automod/bad-words`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const updateAutoModBadWords = async (badWords) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(
    `${API}/guilds/${encodeURIComponent(guildId)}/automod/bad-words`,
    { badWords: Array.isArray(badWords) ? badWords : [] },
    { headers: getHeaders() },
  );
  return response.data;
};

export const getAutoModLinks = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(`${API}/guilds/${encodeURIComponent(guildId)}/automod/links`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const updateAutoModLinks = async ({ allowedLinks = [], blockedLinks = [] } = {}) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(
    `${API}/guilds/${encodeURIComponent(guildId)}/automod/links`,
    {
      allowedLinks: Array.isArray(allowedLinks) ? allowedLinks : [],
      blockedLinks: Array.isArray(blockedLinks) ? blockedLinks : [],
    },
    { headers: getHeaders() },
  );
  return response.data;
};

export const getAutoModExclusions = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(`${API}/guilds/${encodeURIComponent(guildId)}/automod/exclusions`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const updateAutoModExclusions = async ({ disabledChannels = [], disabledRoles = [] } = {}) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(
    `${API}/guilds/${encodeURIComponent(guildId)}/automod/exclusions`,
    {
      disabledChannels: Array.isArray(disabledChannels) ? disabledChannels : [],
      disabledRoles: Array.isArray(disabledRoles) ? disabledRoles : [],
    },
    { headers: getHeaders() },
  );
  return response.data;
};

export const getAutoModChannels = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(`${API}/guilds/${encodeURIComponent(guildId)}/automod/channels`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const updateAutoModChannels = async ({ imageOnlyChannels = [], musicOnlyChannels = [] } = {}) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(
    `${API}/guilds/${encodeURIComponent(guildId)}/automod/channels`,
    {
      imageOnlyChannels: Array.isArray(imageOnlyChannels) ? imageOnlyChannels : [],
      musicOnlyChannels: Array.isArray(musicOnlyChannels) ? musicOnlyChannels : [],
    },
    { headers: getHeaders() },
  );
  return response.data;
};

export const uploadLevelIcon = async (file) => {
  const guildId = await _requireGuildId();
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(
    `${API}/guilds/${encodeURIComponent(guildId)}/level/assets/icon`,
    formData,
    {
      headers: {
        ...getHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

// Levels API
export const getLevels = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/levels`, guildId));
  return response.data;
};

export const createLevel = async (levelData) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(_withGuildId(`${API}/levels`, guildId), levelData, { headers: getHeaders() });
  return response.data;
};

export const updateLevel = async (currentLevel, levelData) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(
    _withGuildId(`${API}/levels/${encodeURIComponent(currentLevel)}`, guildId),
    levelData,
    { headers: getHeaders() }
  );
  return response.data;
};

export const deleteLevel = async (level) => {
  const guildId = await _requireGuildId();
  const response = await axios.delete(_withGuildId(`${API}/levels/${level}`, guildId), { headers: getHeaders() });
  return response.data;
};

// Users API
export const getUsers = async (skip = 0, limit = 50) => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/users?skip=${skip}&limit=${limit}`, guildId), { headers: getHeaders() });
  return response.data;
};

export const getLeaderboard = async (limit = 10) => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/users/leaderboard?limit=${limit}`, guildId));
  return response.data;
};

export const getUser = async (userId) => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/users/${userId}`, guildId));
  return response.data;
};

export const updateUser = async (userId, updates) => {
  const guildId = await _requireGuildId();
  const response = await axios.patch(_withGuildId(`${API}/users/${userId}`, guildId), updates, { headers: getHeaders() });
  return response.data;
};

export const resetUser = async (userId) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(_withGuildId(`${API}/users/${userId}/reset`, guildId), {}, { headers: getHeaders() });
  return response.data;
};

export const resetAllUsers = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.post(_withGuildId(`${API}/users/reset-all`, guildId), {}, { headers: getHeaders() });
  return response.data;
};

export const freezeUserXp = async (userId, { duration = "", reason = "" } = {}) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(
    _withGuildId(`${API}/users/${encodeURIComponent(userId)}/xp-freeze`, guildId),
    { duration, reason },
    { headers: getHeaders() },
  );
  return response.data;
};

export const unfreezeUserXp = async (userId, { reason = "" } = {}) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(
    _withGuildId(`${API}/users/${encodeURIComponent(userId)}/xp-unfreeze`, guildId),
    { reason },
    { headers: getHeaders() },
  );
  return response.data;
};

// Exclusions API
export const getExclusions = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/exclusions`, guildId), { headers: getHeaders() });
  return response.data;
};

export const updateExclusions = async (updates) => {
  const guildId = await _requireGuildId();
  const response = await axios.patch(_withGuildId(`${API}/exclusions`, guildId), updates, { headers: getHeaders() });
  return response.data;
};

// Admin Roles API
export const getAdminRoles = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/admin-roles`, guildId), { headers: getHeaders() });
  return response.data;
};

export const addAdminRole = async (roleId, roleName) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(_withGuildId(`${API}/admin-roles`, guildId), { role_id: roleId, role_name: roleName }, { headers: getHeaders() });
  return response.data;
};

export const removeAdminRole = async (roleId) => {
  const guildId = await _requireGuildId();
  const response = await axios.delete(_withGuildId(`${API}/admin-roles/${roleId}`, guildId), { headers: getHeaders() });
  return response.data;
};

// Dashboard Access Roles API
export const getDashboardAccessRoles = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/dashboard-access/roles`, guildId), { headers: getHeaders() });
  return response.data;
};

export const addDashboardAccessRole = async ({ role_id, role_name = '', scopes = ['*'] }) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(
    _withGuildId(`${API}/dashboard-access/roles`, guildId),
    { role_id, role_name, scopes },
    { headers: getHeaders() }
  );
  if (typeof window !== 'undefined' && window?.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('tns:dashboard-access-updated', { detail: { guildId } }));
  }
  return response.data;
};

export const updateDashboardAccessRole = async (roleId, { role_name, scopes }) => {
  const guildId = await _requireGuildId();
  const payload = {};
  if (role_name !== undefined) payload.role_name = role_name;
  if (scopes !== undefined) payload.scopes = scopes;
  const response = await axios.patch(
    _withGuildId(`${API}/dashboard-access/roles/${encodeURIComponent(roleId)}`, guildId),
    payload,
    { headers: getHeaders() }
  );
  if (typeof window !== 'undefined' && window?.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('tns:dashboard-access-updated', { detail: { guildId } }));
  }
  return response.data;
};

export const removeDashboardAccessRole = async (roleId) => {
  const guildId = await _requireGuildId();
  const response = await axios.delete(
    _withGuildId(`${API}/dashboard-access/roles/${encodeURIComponent(roleId)}`, guildId),
    { headers: getHeaders() }
  );
  if (typeof window !== 'undefined' && window?.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('tns:dashboard-access-updated', { detail: { guildId } }));
  }
  return response.data;
};

// Discord API
export const getBotGuilds = async () => {
  const response = await axios.get(`${API}/discord/guilds`, { headers: getHeaders() });
  return response.data;
};

export const getUserGuilds = async () => {
  const response = await axios.get(`${API}/auth/guilds`, { headers: getHeaders() });
  return response.data;
};

export const getAccessibleGuilds = async ({ signal } = {}) => {
  const response = await axios.get(`${API}/auth/accessible-guilds`, {
    headers: getHeaders(),
    signal,
  });
  return response.data;
};

export const getBotInviteUrl = async (guildId = "") => {
  const response = await axios.get(
    `${API}/auth/bot-invite?guild_id=${encodeURIComponent(guildId || "")}`,
    { headers: getHeaders() }
  );
  return response.data;
};

export const getGuildChannels = async (guildId) => {
  const response = await axios.get(`${API}/discord/guilds/${guildId}/channels`, { headers: getHeaders() });
  return response.data;
};

export const getGuildRoles = async (guildId) => {
  const response = await axios.get(`${API}/discord/guilds/${guildId}/roles`, { headers: getHeaders() });
  return response.data;
};

export const getGuildInfo = async (guildId) => {
  const response = await axios.get(`${API}/discord/guilds/${guildId}`, { headers: getHeaders() });
  return response.data;
};

export const getBotStatus = async () => {
  const response = await axios.get(`${API}/discord/status`);
  return response.data;
};

// User Profile API
export const getUserProfile = async (userId) => {
  const response = await axios.get(`${API}/user/${userId}/profile`, { headers: getHeaders() });
  return response.data;
};

export const getUserSubscription = async (userId) => {
  const response = await axios.get(`${API}/user/${userId}/subscription`, { headers: getHeaders() });
  return response.data;
};

export const getUserActivity = async (userId, { page = 1, limit = 20, logType = '', search = '' } = {}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (logType) params.set('log_type', logType);
  if (search) params.set('search', search);
  const response = await axios.get(`${API}/user/${userId}/activity?${params.toString()}`, { headers: getHeaders() });
  return response.data;
};

export const getUserServers = async (userId, { page = 1, limit = 12, sort = 'name', order = 'asc', search = '' } = {}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (sort) params.set('sort', sort);
  if (order) params.set('order', order);
  if (search) params.set('search', search);
  const response = await axios.get(`${API}/user/${userId}/servers?${params.toString()}`, { headers: getHeaders() });
  return response.data;
};

export const trackUserServerOpen = async (userId, guildId) => {
  const response = await axios.post(
    `${API}/user/${userId}/servers/open`,
    { guild_id: guildId },
    { headers: getHeaders() }
  );
  return response.data;
};

export const getUserBilling = async (userId, { page = 1, limit = 10 } = {}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  const response = await axios.get(`${API}/user/${userId}/billing?${params.toString()}`, { headers: getHeaders() });
  return response.data;
};

export const getSupportFaq = async () => {
  const response = await axios.get(`${API}/support/faq`, { headers: getHeaders() });
  return response.data;
};

// Stats API
export const getStats = async (days = 1) => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/stats?days=${encodeURIComponent(days)}`, guildId), { headers: getHeaders() });
  return response.data;
};

export const getServerOverviewStats = async (guildId, days = 7) => {
  const resolvedGuildId = (guildId || '').toString() || await _requireGuildId();
  const response = await axios.get(
    `${API}/stats/server-overview?guild_id=${encodeURIComponent(resolvedGuildId)}&days=${encodeURIComponent(days)}`,
    { headers: getHeaders() }
  );
  return response.data;
};

// Embeds API
export const listEmbedTemplates = async (guildId = "") => {
  const resolvedGuildId = (guildId || '').toString() || await _requireGuildId();
  const response = await axios.get(
    `${API}/embeds/templates?guild_id=${encodeURIComponent(resolvedGuildId)}`,
    { headers: getHeaders() }
  );
  return response.data;
};

export const createEmbedTemplate = async ({ guild_id, name, embed, components, mention_type = "none" }) => {
  const resolvedGuildId = (guild_id || '').toString() || await _requireGuildId();
  const response = await axios.post(
    `${API}/embeds/templates?guild_id=${encodeURIComponent(resolvedGuildId)}`,
    { name, embed, components, mention_type },
    { headers: getHeaders() }
  );
  return response.data;
};

export const updateEmbedTemplate = async (templateId, updates) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(
    _withGuildId(`${API}/embeds/templates/${templateId}`, guildId),
    updates,
    { headers: getHeaders() }
  );
  return response.data;
};

export const deleteEmbedTemplate = async (templateId) => {
  const guildId = await _requireGuildId();
  const response = await axios.delete(
    _withGuildId(`${API}/embeds/templates/${templateId}`, guildId),
    { headers: getHeaders() }
  );
  return response.data;
};

export const sendEmbed = async ({ channel_id, embed, components, name, template_id, mention_type = "none" }) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(
    _withGuildId(`${API}/embeds/send`, guildId),
    { channel_id, embed, components, name, template_id, mention_type },
    { headers: getHeaders() }
  );
  return response.data;
};

export const getPlainMessageCapabilities = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(
    `${API}/guilds/${encodeURIComponent(guildId)}/messages/plain/capabilities`,
    { headers: getHeaders() }
  );
  return response.data || {};
};

export const getSendableMessageChannels = async (guildId = '', { feature = 'plain' } = {}) => {
  const resolvedGuildId = (guildId || '').toString() || await _requireGuildId();
  const params = new URLSearchParams();
  params.set('feature', feature || 'plain');
  const response = await axios.get(
    `${API}/guilds/${encodeURIComponent(resolvedGuildId)}/messages/plain/sendable-channels?${params.toString()}`,
    { headers: getHeaders() }
  );
  return response.data || {};
};

export const sendPlainMessage = async ({
  channelId,
  content = '',
  silent = false,
  attachments = [],
  poll = null,
  messageMode = 'plain',
  onUploadProgress,
}) => {
  const guildId = await _requireGuildId();
  const payload = { channelId, content, silent, poll, messageMode };
  const files = Array.isArray(attachments) ? attachments.filter(Boolean) : [];
  if (files.length) {
    const formData = new FormData();
    formData.append('payload_json', JSON.stringify(payload));
    files.forEach((file) => formData.append('files', file));
    const response = await axios.post(
      `${API}/guilds/${encodeURIComponent(guildId)}/messages/plain/send`,
      formData,
      {
        headers: getHeaders(),
        onUploadProgress,
      }
    );
    return response.data || {};
  }
  const response = await axios.post(
    `${API}/guilds/${encodeURIComponent(guildId)}/messages/plain/send`,
    payload,
    { headers: getHeaders() }
  );
  return response.data || {};
};

export const uploadEmbedImage = async (file) => {
  const guildId = await _requireGuildId();
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(
    _withGuildId(`${API}/embeds/upload`, guildId),
    formData,
    {
      headers: {
        ...getHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

// Welcome System API
export const getGuildWelcomeSettings = async (guildId = "") => {
  const resolvedGuildId = (guildId || '').toString() || await _requireGuildId();
  const response = await axios.get(
    `${API}/guilds/${encodeURIComponent(resolvedGuildId)}/welcome-settings`,
    { headers: getHeaders() }
  );
  return response.data;
};

export const updateGuildWelcomeSettings = async (guildId = "", payload = {}) => {
  const resolvedGuildId = (guildId || '').toString() || await _requireGuildId();
  const response = await axios.put(
    `${API}/guilds/${encodeURIComponent(resolvedGuildId)}/welcome-settings`,
    payload,
    { headers: getHeaders() }
  );
  const data = response.data || {};
  if (typeof window !== 'undefined' && window?.dispatchEvent) {
    window.dispatchEvent(
      new CustomEvent('tns:welcome-settings-updated', {
        detail: { guildId: resolvedGuildId, settings: data },
      })
    );
  }
  return data;
};

export const uploadWelcomeBackground = async (file, guildId = "") => {
  const resolvedGuildId = (guildId || '').toString() || await _requireGuildId();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('guild_id', resolvedGuildId);
  const response = await axios.post(
    `${API}/upload/welcome-background`,
    formData,
    {
      headers: {
        ...getHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const testWelcomeRender = async (guildId = "") => {
  const resolvedGuildId = (guildId || '').toString() || await _requireGuildId();
  const response = await axios.post(
    `${API}/test/welcome-render`,
    { guildId: resolvedGuildId },
    {
      headers: getHeaders(),
      responseType: 'blob',
    }
  );
  return response.data;
};

export const listSentEmbeds = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/embeds/sent`, guildId), { headers: getHeaders() });
  return response.data;
};

export const updateSentEmbed = async (sentId, payload) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(
    _withGuildId(`${API}/embeds/sent/${sentId}`, guildId),
    payload,
    { headers: getHeaders() }
  );
  return response.data;
};

export const deleteSentEmbed = async (sentId) => {
  const guildId = await _requireGuildId();
  const response = await axios.delete(
    _withGuildId(`${API}/embeds/sent/${sentId}`, guildId),
    { headers: getHeaders() }
  );
  return response.data;
};

// XP Bonus قواعد
export const getRoleBonusRules = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/xp-bonus/roles`, guildId), { headers: getHeaders() });
  return response.data;
};

export const createRoleBonusRule = async (payload) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(_withGuildId(`${API}/xp-bonus/roles`, guildId), payload, { headers: getHeaders() });
  return response.data;
};

export const updateRoleBonusRule = async (ruleId, updates) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(_withGuildId(`${API}/xp-bonus/roles/${ruleId}`, guildId), updates, { headers: getHeaders() });
  return response.data;
};

export const toggleRoleBonusRule = async (ruleId, enabled) => {
  const guildId = await _requireGuildId();
  const response = await axios.patch(_withGuildId(`${API}/xp-bonus/roles/${ruleId}/toggle`, guildId), { enabled }, { headers: getHeaders() });
  return response.data;
};

export const deleteRoleBonusRule = async (ruleId) => {
  const guildId = await _requireGuildId();
  const response = await axios.delete(_withGuildId(`${API}/xp-bonus/roles/${ruleId}`, guildId), { headers: getHeaders() });
  return response.data;
};

export const getVoiceBonusRules = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/xp-bonus/voice`, guildId), { headers: getHeaders() });
  return response.data;
};

export const createVoiceBonusRule = async (payload) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(_withGuildId(`${API}/xp-bonus/voice`, guildId), payload, { headers: getHeaders() });
  return response.data;
};

export const updateVoiceBonusRule = async (ruleId, updates) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(_withGuildId(`${API}/xp-bonus/voice/${ruleId}`, guildId), updates, { headers: getHeaders() });
  return response.data;
};

export const toggleVoiceBonusRule = async (ruleId, enabled) => {
  const guildId = await _requireGuildId();
  const response = await axios.patch(_withGuildId(`${API}/xp-bonus/voice/${ruleId}/toggle`, guildId), { enabled }, { headers: getHeaders() });
  return response.data;
};

export const deleteVoiceBonusRule = async (ruleId) => {
  const guildId = await _requireGuildId();
  const response = await axios.delete(_withGuildId(`${API}/xp-bonus/voice/${ruleId}`, guildId), { headers: getHeaders() });
  return response.data;
};

export const getTextBonusRules = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/xp-bonus/text`, guildId), { headers: getHeaders() });
  return response.data;
};

export const createTextBonusRule = async (payload) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(_withGuildId(`${API}/xp-bonus/text`, guildId), payload, { headers: getHeaders() });
  return response.data;
};

export const updateTextBonusRule = async (ruleId, updates) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(_withGuildId(`${API}/xp-bonus/text/${ruleId}`, guildId), updates, { headers: getHeaders() });
  return response.data;
};

export const toggleTextBonusRule = async (ruleId, enabled) => {
  const guildId = await _requireGuildId();
  const response = await axios.patch(_withGuildId(`${API}/xp-bonus/text/${ruleId}/toggle`, guildId), { enabled }, { headers: getHeaders() });
  return response.data;
};

export const deleteTextBonusRule = async (ruleId) => {
  const guildId = await _requireGuildId();
  const response = await axios.delete(_withGuildId(`${API}/xp-bonus/text/${ruleId}`, guildId), { headers: getHeaders() });
  return response.data;
};

// XP Events
export const getXpEvents = async () => {
  const guildId = await _requireGuildId();
  const response = await axios.get(_withGuildId(`${API}/xp-events`, guildId), { headers: getHeaders() });
  return response.data;
};

export const createXpEvent = async (payload) => {
  const guildId = await _requireGuildId();
  const response = await axios.post(_withGuildId(`${API}/xp-events/create`, guildId), payload, { headers: getHeaders() });
  return response.data;
};

export const updateXpEvent = async (payload) => {
  const guildId = await _requireGuildId();
  const response = await axios.put(_withGuildId(`${API}/xp-events/update`, guildId), payload, { headers: getHeaders() });
  return response.data;
};

export const toggleXpEvent = async (eventId, enabled) => {
  const guildId = await _requireGuildId();
  const response = await axios.patch(_withGuildId(`${API}/xp-events/toggle`, guildId), { event_id: eventId, enabled }, { headers: getHeaders() });
  return response.data;
};

export const deleteXpEvent = async (eventId) => {
  const guildId = await _requireGuildId();
  const response = await axios.delete(_withGuildId(`${API}/xp-events/delete?event_id=${encodeURIComponent(eventId)}`, guildId), { headers: getHeaders() });
  return response.data;
};
